import { ArrowRight } from "lucide-react";
import { motion, useReducedMotion, type Variants } from "motion/react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { REVEAL_EASE } from "@/src/common/components/animated-section";
import { CtaAnchor } from "@/src/common/components/cta-link";
import { EyebrowBadge } from "@/src/common/components/eyebrow-badge";
import { ProductDemo } from "@/src/common/components/product-demo/product-demo";
import { Reveal } from "@/src/common/components/reveal";
import { SectionHeading } from "@/src/common/components/section-heading";
import { APP_LINKS } from "@/src/common/constants/app-links";
import { toEntranceVariant, useEntrance } from "@/src/common/hooks/use-entrance";

function createStageVariants(reduced: boolean): Variants {
	return {
		hidden: { opacity: reduced ? 1 : 0, scale: reduced ? 1 : 0.97, rotateX: reduced ? 0 : 6, transition: { duration: 0 } },
		visible: { opacity: 1, scale: 1, rotateX: 0, transition: reduced ? { duration: 0 } : { duration: 0.8, ease: REVEAL_EASE } },
	};
}

export function InteractiveDemoSection() {
	const { t } = useTranslation("home");
	const reduced = useReducedMotion() === true;
	const stageRef = useRef<HTMLDivElement>(null);
	const entrance = useEntrance(stageRef, { amount: 0.2, disabled: reduced });

	return (
		<section id="demo" className="bg-content1/40 py-24 md:py-32">
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<SectionHeading
					eyebrow={<EyebrowBadge tone="ink">{t("demo.eyebrow")}</EyebrowBadge>}
					title={t("demo.title")}
					titleAccent={t("demo.title_accent")}
					description={t("demo.subtitle")}
				/>

				<div className="mt-12 md:[perspective:1400px] lg:mt-16">
					<motion.div
						ref={stageRef}
						initial={false}
						animate={toEntranceVariant(entrance)}
						variants={createStageVariants(reduced)}
						style={{ transformOrigin: "50% 100%" }}
					>
						<ProductDemo />
					</motion.div>
				</div>

				<Reveal delay={0.1} className="mt-8 flex flex-col items-center gap-4 text-center">
					<p className="text-sm text-default-500">{t("demo.footnote")}</p>
					<CtaAnchor href={APP_LINKS.SIGN_UP} variant="gradient" size="md" endContent={<ArrowRight className="h-4 w-4" />}>
						{t("demo.footnote_cta")}
					</CtaAnchor>
				</Reveal>
			</div>
		</section>
	);
}
